import { useState, useEffect } from 'react';
import { RootState } from '../../../store/store';
import { useTranslation } from 'react-i18next';
import '../../../styles/pages/settings/settings.scss';
import { useSelector } from 'react-redux';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { HOME_ROUTE } from '../../../lib/constants/route';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import AITextArea from '../../../ui/inputs/AITextArea';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import get_tara_custom_instructions_api_call, {
  save_tara_custom_instructions_api_call,
  update_tara_custom_instructions_settings_api_call,
  TaraCustomInstructionsSettings,
} from '../../../lib/api/settings/tara-custom-instructions-api-call';
import { aiGenerateSVG } from '../../../lib/allCustomSVGs';

const MAX_INSTRUCTIONS_LENGTH = 300;

const toggleConfigs: Array<{
  key: keyof TaraCustomInstructionsSettings;
  titleKey: string;
  descriptionKey: string;
  id: string;
}> = [
  {
    key: 'chat',
    titleKey: 'Chat',
    descriptionKey: 'ApplyInstructionsToChatConversations',
    id: 'tara-settings-chat-toggle',
  },
  {
    key: 'notifications',
    titleKey: 'Notifications',
    descriptionKey: 'ApplyInstructionsToAIGeneratedNotifications',
    id: 'tara-settings-notifications-toggle',
  },
  {
    key: 'reports',
    titleKey: 'Reports',
    descriptionKey: 'ApplyInstructionsToAutomatedReportGeneration',
    id: 'tara-settings-reports-toggle',
  },
  {
    key: 'custom_prompts',
    titleKey: 'CustomPrompts',
    descriptionKey: 'ApplyInstructionsToCustomPromptResponses',
    id: 'tara-settings-custom-prompts-toggle',
  },
  {
    key: 'graphs',
    titleKey: 'Graphs',
    descriptionKey: 'ApplyInstructionsToGraphInsightsAndAnalysis',
    id: 'tara-settings-graphs-toggle',
  },
];

const SettingsTara = () => {
  const { t } = useTranslation();

  const isWhiteLabel = useSelector((state: RootState) => state.UI.isWhiteLabel);
  const shopSlug = useSelector(
    (state: RootState) => state.user?.company?.shop_slug
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [instructions, setInstructions] = useState<string>('');
  const [settings, setSettings] = useState<TaraCustomInstructionsSettings>({
    chat: false,
    notifications: false,
    reports: false,
    custom_prompts: false,
    graphs: false,
  });
  const [isSavingInstructions, setIsSavingInstructions] =
    useState<boolean>(false);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState<boolean>(false);

  useEffect(() => {
    loadCustomInstructions();
  }, [shopSlug]);

  const loadCustomInstructions = async () => {
    if (!shopSlug) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const data = await get_tara_custom_instructions_api_call();
      if (data) {
        setInstructions(data.instructions || '');
        setSettings(
          data.settings || {
            chat: false,
            notifications: false,
            reports: false,
            custom_prompts: false,
            graphs: false,
          }
        );
      }
    } catch (error) {
      console.error('Failed to load custom instructions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveInstructions = async () => {
    setIsSavingInstructions(true);
    try {
      await save_tara_custom_instructions_api_call(instructions);
    } catch (error) {
      console.error('Failed to save instructions:', error);
    } finally {
      setIsSavingInstructions(false);
    }
  };

  const handleToggleChange = async (
    key: keyof TaraCustomInstructionsSettings,
    value: boolean
  ) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);

    setIsUpdatingSettings(true);
    try {
      const success = await update_tara_custom_instructions_settings_api_call(
        newSettings
      );
      if (!success) {
        // Revert on failure
        setSettings(settings);
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
      // Revert on error
      setSettings(settings);
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const characterCount = instructions.length;
  const isInstructionsDisabled =
    characterCount > MAX_INSTRUCTIONS_LENGTH ||
    isSavingInstructions ||
    isUpdatingSettings;

  return (
    <SubPageInnerContentHolder
      title={t('TaraAISettings')}
      backBtnLink={isWhiteLabel ? undefined : HOME_ROUTE}
    >
      <div style={{ padding: '0 2rem' }}>
        {isLoading ? (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Tara AI Configuration Card */}
            <SettingsCommonCard
              mainTitle={t('TaraAIConfiguration')}
              subTitle={t(
                'CustomizeHowTaraAIAssistantBehavesAcrossYourPlatform'
              )}
              mainIconSvg={aiGenerateSVG}
              mainIconSvgFillColor='var(--main-text-color)'
              styles={{ marginTop: '2rem' }}
            />

            {/* Custom Instructions Card */}
            <SettingsCommonCard
              mainTitle={t('CustomInstructions')}
              subTitle={t(
                'TheseInstructionsWillBeAppendedToSelectedTaraBasedActionsOnOurPlatform'
              )}
              styles={{ marginTop: '3rem' }}
            >
              <div style={{ marginTop: '2rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                  }}
                >
                  <label
                    htmlFor='tara-custom-instructions'
                    style={{
                      fontSize: '1.4rem',
                      fontFamily: 'var(--main-font-primary)',
                      color: 'var(--main-text-color)',
                      fontWeight: 500,
                    }}
                  >
                    {t('Instructions')}
                  </label>
                  <span
                    style={{
                      fontSize: '1.2rem',
                      color:
                        characterCount > MAX_INSTRUCTIONS_LENGTH
                          ? '#dc2626'
                          : 'var(--sidebar-sublink-gray-text-color)',
                      fontFamily: 'var(--main-font-secondary)',
                    }}
                  >
                    {characterCount}/{MAX_INSTRUCTIONS_LENGTH}
                  </span>
                </div>

                <div style={{ position: 'relative' }}>
                  <AITextArea
                    id='tara-custom-instructions'
                    placeholder=''
                    innerPlaceholder={t('EnterCustomInstructionsForTaraAI')}
                    value={instructions}
                    changeListeners={[
                      (e) => {
                        const value = e.target.value;
                        setInstructions(value);
                      },
                    ]}
                    readonly={isSavingInstructions}
                  />
                </div>

                <p
                  style={{
                    marginTop: '1rem',
                    fontSize: '1.3rem',
                    color: 'var(--sidebar-sublink-gray-text-color)',
                    fontFamily: 'var(--main-font-secondary)',
                    lineHeight: '1.6rem',
                  }}
                >
                  {t(
                    'ProvideSpecificGuidelinesForHowTaraShouldRespondAndBehaveInYourAccount'
                  )}
                </p>

                <div style={{ marginTop: '2rem' }}>
                  <MainSquareButton
                    value={t('SaveInstructions')}
                    type='button'
                    disabled={isInstructionsDisabled}
                    loading={isSavingInstructions}
                    onClick={handleSaveInstructions}
                    classes='tara-save-instructions-button'
                  />
                </div>
              </div>
            </SettingsCommonCard>

            {/* Apply to Modules Card */}
            <SettingsCommonCard
              mainTitle={t('ApplyToModules')}
              subTitle={t('SelectWhichModulesShouldUseYourCustomInstructions')}
              styles={{ marginTop: '3rem' }}
            >
              <div
                className='itemWithRightAction-container'
                style={{ marginTop: '2rem' }}
              >
                {toggleConfigs.map((config) => (
                  <div key={config.key} className='itemWithRightAction'>
                    <div className='itemWithRightAction__left'>
                      <h3 style={{ marginBottom: '0.5rem' }}>
                        {t(config.titleKey)}
                      </h3>
                      <p
                        style={{
                          marginTop: '0.5rem',
                          fontWeight: 300,
                          marginBottom: 0,
                        }}
                      >
                        {t(config.descriptionKey)}
                      </p>
                    </div>
                    <div className='itemWithRightAction__right'>
                      <div className='itemWithRightAction__right--greenToggle'>
                        <ToggleSwitch
                          isOn={settings[config.key]}
                          onClick={(value: boolean) =>
                            handleToggleChange(config.key, value)
                          }
                          disabled={isUpdatingSettings}
                          id={config.id}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SettingsCommonCard>
          </>
        )}
      </div>
    </SubPageInnerContentHolder>
  );
};

export default SettingsTara;
